import { useState } from 'react';
import { Head, Link } from '@inertiajs/react';

/**
 * Book data from the backend.
 */
interface TomeBook {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    hue: number;
    cover_image_url: string | null;
    is_published: boolean;
    chapters_count: number;
    pages_count: number;
}

/**
 * Props for the Bookshelf page.
 */
interface Props {
    books: TomeBook[];
    isGm: boolean;
}

/**
 * Public Bookshelf page - displays all published books in a visual bookshelf layout.
 * Books appear as vertical spines on a shelf, and expand to show cover on hover.
 * This is the landing page for the Archivist's Tome.
 */
export default function Bookshelf({ books, isGm }: Props) {
    return (
        <>
            <Head title="Archivist's Tome - Bookshelf" />

            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
                {/* Header */}
                <header className="bg-slate-800/80 border-b border-amber-900/30">
                    <div className="max-w-7xl mx-auto px-4 py-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-serif text-amber-200 flex items-center gap-3">
                                    📚 The Archivist's Tome
                                </h1>
                                <p className="text-amber-200/60 mt-1">
                                    A repository of lore and knowledge
                                </p>
                            </div>
                            {/*<Link*/}
                            {/*    href="/"*/}
                            {/*    className="text-amber-200/60 hover:text-amber-200 transition-colors"*/}
                            {/*>*/}
                            {/*    ← Return to the realm*/}
                            {/*</Link>*/}
                        </div>
                    </div>
                </header>

                {/* Bookshelf Content */}
                <main className="max-w-7xl mx-auto px-4 py-8">
                    {books.length === 0 ? (
                        <EmptyBookshelf />
                    ) : (
                        <>
                            {/*
                             * Mobile layout: Grid of portrait book covers (cards).
                             * Uses a responsive grid that shows 2 books per row on small screens.
                             * Hidden on md and above where we show the shelf layout.
                             */}
                            <div className="md:hidden grid grid-cols-2 gap-4">
                                {books.map((book) => (
                                    <BookCard key={book.id} book={book} isGm={isGm} />
                                ))}
                            </div>

                            {/*
                             * Desktop layout: Wooden shelf with book spines.
                             * Hidden on mobile (below md breakpoint).
                             */}
                            <div className="hidden md:block relative">
                                {/*
                                 * Books container - horizontal row of spines.
                                 * pt-12 provides space for the expanded book cover which translates up by 16px
                                 * and is taller than the spine (h-80 = 320px vs h-72 = 288px).
                                 * min-h-[360px] accounts for spine height (288px) + top padding (48px) + bottom padding.
                                 */}
                                <div className="flex items-end gap-1 pt-12 pb-4 overflow-x-auto min-h-[360px]">
                                    {books.map((book) => (
                                        <BookSpine key={book.id} book={book} isGm={isGm} />
                                    ))}
                                </div>
                                {/* Shelf surface */}
                                <div className="h-4 bg-gradient-to-b from-amber-800 to-amber-900 rounded-b shadow-lg" />
                                {/* Shelf bracket shadow */}
                                <div className="h-2 bg-amber-950/50 rounded-b mx-4" />
                            </div>
                        </>
                    )}
                </main>
            </div>
        </>
    );
}

/**
 * Empty state when no books exist.
 */
function EmptyBookshelf() {
    return (
        <div className="text-center py-16">
            <div className="text-6xl mb-6">📚</div>
            <h2 className="text-2xl font-serif text-amber-200 mb-4">
                The Bookshelf Awaits
            </h2>
            <p className="text-amber-200/70 max-w-lg mx-auto">
                The shelves are empty, waiting to be filled with tales of adventure,
                histories of the realm, and secrets yet to be discovered.
            </p>
        </div>
    );
}

/**
 * BookSpine - Visual representation of a book on the shelf.
 * Default state: Vertical spine with rotated title (reads top to bottom).
 * Hover state: Expands to show book cover floating above adjacent books.
 * Uses the book's hue for accent color.
 *
 * The spine maintains a fixed width in the flex layout. The expanded cover
 * is positioned absolutely so it overlays on top of adjacent books without
 * causing layout shift.
 */
function BookSpine({ book, isGm }: { book: TomeBook; isGm: boolean }) {
    const [isHovered, setIsHovered] = useState(false);

    // Generate colors from hue
    const accentColor = `hsl(${book.hue}, 50%, 35%)`;
    const accentColorLight = `hsl(${book.hue}, 60%, 45%)`;
    const accentColorDark = `hsl(${book.hue}, 40%, 25%)`;
    const accentColorVeryDark = `hsl(${book.hue}, 35%, 18%)`;

    return (
        <Link
            href={`/tome/${book.slug}`}
            className="relative flex-shrink-0 cursor-pointer"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                // Fixed width in flex layout - spine width only
                width: '48px',
                // Raise z-index when hovered so cover appears above adjacent books
                zIndex: isHovered ? 50 : 1,
            }}
        >
            {/* Fixed-size spine container - always visible, maintains layout */}
            <div className="relative h-72 w-12 rounded-t">
                {/* Book spine (always rendered, fades on hover) */}
                <div
                    className="absolute inset-0 transition-opacity duration-300 rounded-t"
                    style={{
                        opacity: isHovered ? 0 : 1,
                        background: `linear-gradient(90deg, ${accentColorVeryDark} 0%, ${accentColorDark} 15%, ${accentColor} 50%, ${accentColorDark} 85%, ${accentColorVeryDark} 100%)`,
                    }}
                >
                    {/* Spine edge highlights */}
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-white/10 rounded-tl" />
                    <div className="absolute right-0 top-0 bottom-0 w-1 bg-black/20 rounded-tr" />

                    {/* Rotated title - reads top to bottom */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span
                            className="text-white font-serif font-bold text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[260px] px-2"
                            style={{
                                writingMode: 'vertical-rl',
                                textOrientation: 'mixed',
                                transform: 'rotate(180deg)',
                            }}
                        >
                            {book.title}
                        </span>
                    </div>

                    {/* Draft indicator on spine */}
                    {isGm && !book.is_published && (
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-2 h-2 bg-yellow-500 rounded-full" />
                    )}
                </div>
            </div>

            {/* Book cover (hover state) - positioned absolutely to overlay adjacent books */}
            <div
                className="absolute left-0 bottom-0 w-48 h-80 transition-all duration-300 ease-out flex flex-col rounded-t pointer-events-none"
                style={{
                    opacity: isHovered ? 1 : 0,
                    transform: isHovered ? 'translateY(-16px)' : 'translateY(0)',
                    background: `linear-gradient(135deg, ${accentColorDark} 0%, ${accentColor} 50%, ${accentColorLight} 100%)`,
                    // Drop shadow for depth when floating above shelf
                    boxShadow: isHovered
                        ? '0 20px 40px rgba(0, 0, 0, 0.5), 0 10px 20px rgba(0, 0, 0, 0.3), -4px 0 15px rgba(0, 0, 0, 0.2), 4px 0 15px rgba(0, 0, 0, 0.2)'
                        : 'none',
                }}
            >
                {/* Cover border/frame */}
                <div className="absolute inset-2 border-2 border-white/20 rounded pointer-events-none" />

                {/* Cover content */}
                <div className="flex-1 p-4 flex flex-col justify-between">
                    {/* Title and description */}
                    <div>
                        <h3 className="text-lg font-serif text-white font-bold leading-tight">
                            {book.title}
                        </h3>
                        {book.description && (
                            <p className="text-white/70 text-xs mt-2 line-clamp-4">
                                {book.description}
                            </p>
                        )}
                    </div>

                    {/* Stats */}
                    <div className="text-white/60 text-xs space-y-1">
                        <div className="flex items-center gap-1">
                            <span>📑</span>
                            <span>{book.chapters_count} chapter{book.chapters_count !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span>📄</span>
                            <span>{book.pages_count} page{book.pages_count !== 1 ? 's' : ''}</span>
                        </div>
                    </div>
                </div>

                {/* Draft indicator on cover */}
                {isGm && !book.is_published && (
                    <div className="absolute top-3 right-3 px-2 py-0.5 bg-yellow-600 text-white text-xs rounded">
                        Draft
                    </div>
                )}

                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-white/5 pointer-events-none rounded-t" />
            </div>
        </Link>
    );
}

/**
 * BookCard - Mobile-friendly book display as a portrait book cover card.
 * Used on mobile devices where hover interactions don't work well.
 * Displays the book cover with title, description, and stats in a portrait layout
 * that resembles a real book cover (taller than wide).
 * Uses the book's hue for accent color.
 */
function BookCard({ book, isGm }: { book: TomeBook; isGm: boolean }) {
    // Generate colors from hue
    const accentColor = `hsl(${book.hue}, 50%, 35%)`;
    const accentColorLight = `hsl(${book.hue}, 60%, 45%)`;
    const accentColorDark = `hsl(${book.hue}, 40%, 25%)`;

    return (
        <Link
            href={`/tome/${book.slug}`}
            className="block aspect-[2/3] rounded-lg overflow-hidden shadow-lg active:scale-[0.98] transition-transform"
            style={{
                background: `linear-gradient(135deg, ${accentColorDark} 0%, ${accentColor} 50%, ${accentColorLight} 100%)`,
            }}
        >
            {/* Card content - flex column to distribute content vertically */}
            <div className="relative h-full p-4 flex flex-col">
                {/* Decorative border/frame */}
                <div className="absolute inset-2 border-2 border-white/20 rounded pointer-events-none" />

                {/* Draft indicator */}
                {isGm && !book.is_published && (
                    <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-yellow-600 text-white text-[10px] rounded z-20">
                        Draft
                    </div>
                )}

                {/* Title section - top of cover */}
                <div className="relative z-10 flex-1">
                    <h3 className="text-base font-serif text-white font-bold leading-tight line-clamp-3">
                        {book.title}
                    </h3>
                </div>

                {/* Description - middle of cover */}
                {book.description && (
                    <div className="relative z-10 flex-1">
                        <p className="text-white/70 text-xs line-clamp-4">
                            {book.description}
                        </p>
                    </div>
                )}

                {/* Stats - bottom of cover */}
                <div className="relative z-10 mt-auto text-white/60 text-xs space-y-0.5">
                    <div className="flex items-center gap-1">
                        <span>📑</span>
                        <span>{book.chapters_count} ch.</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span>📄</span>
                        <span>{book.pages_count} pg.</span>
                    </div>
                </div>
            </div>
        </Link>
    );
}
